import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from "antd";
import { deleteData, getData, postData, putData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ListTable from "common/ListTable";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { ITEM_TYPE_CREATE_PERMISSION } from "../../routes/permissions";
import { hasPermission } from "../../utils/functions";

const ItemType = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({ name: "", status: "" });

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  // Fetch data from API
  const fetchData = async (page = 1, filterValues = filters) => {
    setTableLoading(true);
    setTableError(null);
    try {
      let query = `?page=${page}`;
      if (filterValues.name)
        query += `&name=${encodeURIComponent(filterValues.name)}`;
      if (filterValues.status !== "" && filterValues.status !== undefined)
        query += `&status=${encodeURIComponent(filterValues.status)}`;
      const res = await getData(`/api/item-types${query}`);
      setData(res.data?.data?.data || []);
      setPagination({
        current: res.data?.data?.current_page || 1,
        pageSize: res.data?.data?.per_page || 10,
        total: res.data?.data?.total || 0,
      });
    } catch (e) {
      message.error("Failed to fetch item types");
      setTableError("Could not load this list.");
    }
    setTableLoading(false);
  };

  useEffect(() => {
    fetchData(1, filters);
    // eslint-disable-next-line
  }, []);

  // Filter form handlers
  const [filterForm] = Form.useForm();
  const handleFilter = () => {
    const values = filterForm.getFieldsValue();
    setFilters(values);
    fetchData(1, values);
  };
  const handleResetFilter = () => {
    filterForm.resetFields();
    setFilters({ name: "", status: "" });
    fetchData(1, { name: "", status: "" });
  };

  // CRUD Handlers
  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({ ...record, status: record.status === "1" });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    setTableLoading(true);
    try {
      await deleteData(`/api/item-types/delete/${id}`);
      message.success("Deleted");
      fetchData();
    } catch (e) {
      message.error("Delete failed");
      setTableLoading(false);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setModalLoading(true);
      const body = {
        ...values,
        status: values.status ? "1" : "0",
      };
      if (editingItem) {
        await putData(`/api/item-types/edit/${editingItem.id}`, body);
        message.success("Updated");
      } else {
        await postData("/api/item-types/add", body);
        message.success("Created");
      }
      setModalVisible(false);
      fetchData();
    } catch (e) {
      message.error("Operation failed");
    }
    setModalLoading(false);
  };

  const handleModalCancel = () => {
    setModalVisible(false);
  };

  // Table columns
  const columns = [
    { title: "Name", dataIndex: "name" },
    {
      title: "Status",
      dataIndex: "status",
      render: (val) => (val === "1" ? "Active" : "Inactive"),
    },
    {
      title: "Action",
      width: "5%",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <Space>
                <Button
                  type={showFilter ? "danger" : "primary"}
                  size="small"
                  onClick={() => setShowFilter((prev) => !prev)}
                  style={{ float: "right" }}
                >
                  {`${showFilter ? "Close" : "Open"} Filter Option`}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleAdd}
                  disabled={!hasPermission([ITEM_TYPE_CREATE_PERMISSION])}
                >
                  {hasPermission([ITEM_TYPE_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"}
                </Button>
              </Space>
            }
            title={<Typography.Title level={5}>Item Types</Typography.Title>}
          />
        }
        content={
          <>
            {showFilter && (
              <Form
                form={filterForm}
                layout="vertical"
                style={{ padding: "4px 10px" }}
                onFinish={handleFilter}
              >
                <Row gutter={24} align={"middle"}>
                  <Col span={3}>
                    <Form.Item name="name" label="Name">
                      <Input placeholder="Search by name" allowClear />
                    </Form.Item>
                  </Col>
                  {/* <Col span={3}>
                    <Form.Item name="status" label="Status" initialValue="">
                      <Select allowClear placeholder="Select status">
                        <Select.Option value="active">Active</Select.Option>
                        <Select.Option value="inactive">Inactive</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col> */}
                  <Col span={2}>
                    <Space>
                      <Form.Item label=" ">
                        <Button type="primary" htmlType="submit" size="small">
                          Filter
                        </Button>
                      </Form.Item>
                      <Form.Item label=" ">
                        <Button onClick={handleResetFilter} size="small">
                          Reset
                        </Button>
                      </Form.Item>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}
            <ListTable
              dataSource={data}
              columns={columns}
              rowKey="id"
              style={{ marginTop: 16 }}
              bordered
              loading={tableLoading}
              error={tableError}
              onRetry={() => fetchData(pagination.current, filters)}
              emptyText="No records to show yet."
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: false,
                onChange: (page) => {
                  setPagination((prev) => ({ ...prev, current: page }));
                  fetchData(page, filters);
                },
              }}
            />
          </>
        }
      />

      <Modal
        open={modalVisible}
        title={editingItem ? "Edit Item Type" : "Add Item Type"}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        destroyOnClose
        confirmLoading={modalLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please input name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input />
          </Form.Item>
          {/* give me switch status */}
          <Form.Item
            name="status"
            label="Status"
            valuePropName="checked"
            rules={[{ required: true, message: "Please select status" }]}
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ItemType;
